/**
 * VendorList
 *
 * Purpose:
 * - Unified vendor list (suppliers/contractors) with active/inactive sub-tabs.
 * - Loads both sub-tab datasets once per main tab and caches in memory.
 * - Preserves URL state and avoids redundant API calls on sub-tab switches.
 */
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import idx from "idx";
import { fetchAccountingConnections } from "../../slice/accounting.slice";

import {
    VENDOR,
    SUPPLIERS,
    CONTRACTORS,
} from "../../constants/route-paths";
import {
    getVendorList,
    changeVendorStatus,
} from "../../actions/vendor-management.action";
import { Link } from "react-router-dom";
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import CustomTable from '../../components/table/table';
import SyncContactsModal from "../../components/sync-contacts-modal";
import './vendor.scss';
import { VENDOR_TYPE, VENDOR_URL_PARAM, VENDOR_TYPE_LABEL, VENDOR_TAB } from "../../constants/vendor.contant";

function VendorList({ rSupplierPermission, wSupplierPermission, rContractorPermission, wContractorPermission, writePermission }) {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const user = useSelector(state => state.user.userData);
    const accountId = idx(user, _ => _.selected_account.id);
    const entitlements = useSelector((state) => state.user.entitlements) || {};
    const isAccountingEnabled = entitlements.accountingIntegration !== false && (entitlements.accountingPush !== false || entitlements.accountingPull !== false);
    const connections = useSelector((state) => state.accounting?.connections) || [];
    const [showSyncModal, setShowSyncModal] = useState(false);

    useEffect(() => {
        if (accountId) {
            dispatch(fetchAccountingConnections());
        }
    }, [accountId, dispatch]);


    const handleSyncContacts = () => {
        setShowSyncModal(true);
    };

    const handleSyncSuccess = () => {
        if (state.activeTab === SUPPLIERS) {
            fetchedRef.current.suppliers = false;
            getSupplierList();
        } else {
            fetchedRef.current.contractors = false;
            getSubContractorList();
        }
    };

    // Prevent duplicate fetches
    const loadingRef = useRef({ suppliers: false, contractors: false });
    const fetchedRef = useRef({ suppliers: false, contractors: false });

    // Get URL parameters for tab state
    const urlSubTab = searchParams.get('subTab');
    const urlTab = searchParams.get('tab');

    // Get tab state from URL or default
    const getTabState = () => {
        let activeTab = urlTab || (rSupplierPermission ? SUPPLIERS : rContractorPermission ? CONTRACTORS : SUPPLIERS);
        let activeSubTab = urlSubTab || "active";

        // Check if user has permission for the requested tab
        if (activeTab === SUPPLIERS && !rSupplierPermission) activeTab = CONTRACTORS;
        else if (activeTab === CONTRACTORS && !rContractorPermission) activeTab = SUPPLIERS;

        return { activeTab, activeSubTab };
    };

    const [state, setState] = useState({
        activeSuppliers: [],
        inactiveSuppliers: [],
        activeContractors: [],
        inactiveContractors: [],
        statusType: "",
        ...getTabState()
    })

    useEffect(() => {
        // Determine effective tab/subtab from URL or defaults
        const effectiveTab = urlTab || (rSupplierPermission ? SUPPLIERS : rContractorPermission ? CONTRACTORS : SUPPLIERS);
        const effectiveSubTab = urlSubTab || "active";

        // Ensure URL reflects defaults when missing; skip fetch in this run to avoid double
        if (!urlTab || !urlSubTab) {
            updateURL(effectiveTab, effectiveSubTab);
            return;
        }

        // Fetch once per main tab when data not yet loaded
        if (effectiveTab === SUPPLIERS && rSupplierPermission) {
            if (!fetchedRef.current.suppliers && state.activeSuppliers.length === 0 && state.inactiveSuppliers.length === 0) getSupplierList();
        } else if (effectiveTab === CONTRACTORS && rContractorPermission) {
            if (!fetchedRef.current.contractors && state.activeContractors.length === 0 && state.inactiveContractors.length === 0) getSubContractorList();
        }
    }, [urlTab, urlSubTab, rSupplierPermission, rContractorPermission])

    const formatTableData = (vendors = []) => {
        const data = vendors.map((vendor) => {
            let primary = (vendor.contacts || []).find(item => item.primary == true) || {};
            return {
                vendorNumber: vendor.vendorNumber,
                businessName: vendor.businessName,
                id: vendor.id,
                address: vendor.address,
                contacts: [primary.firstName, primary.lastName].filter(item => item).join(' ') || '',
                email: vendor.email,
                phoneNumber: vendor.phoneNumber,
                abn: vendor.abn || '',
                paymentTerms: vendor.paymentTerms,
                type: vendor.type,
                isActive: vendor.isActive,
            };
        });
        return data;
    };

    const getSupplierList = async () => {
        if (loadingRef.current.suppliers || fetchedRef.current.suppliers) return;
        loadingRef.current.suppliers = true;
        try {
            // Call API for active suppliers
            const activeSuppliers = await dispatch(getVendorList('Supplier', 'ACTIVE'));
            // Call API for inactive suppliers
            const inactiveSuppliers = await dispatch(getVendorList('Supplier', 'INACTIVE'));

            setState(prev => ({
                ...prev,
                activeSuppliers: formatTableData(activeSuppliers),
                inactiveSuppliers: formatTableData(inactiveSuppliers),
            }));
            fetchedRef.current.suppliers = true;
        } catch (e) {
            console.error(e);
        } finally {
            loadingRef.current.suppliers = false;
        }
    };

    const getSubContractorList = async () => {
        if (loadingRef.current.contractors || fetchedRef.current.contractors) return;
        loadingRef.current.contractors = true;
        try {
            // Call API for active contractors
            const activeContractors = await dispatch(getVendorList('sub-contractor', 'ACTIVE'));
            // Call API for inactive contractors
            const inactiveContractors = await dispatch(getVendorList('sub-contractor', 'INACTIVE'));

            setState(prev => ({
                ...prev,
                activeContractors: formatTableData(activeContractors),
                inactiveContractors: formatTableData(inactiveContractors),
            }));
            fetchedRef.current.contractors = true;
        } catch (e) {
            console.error(e);
        } finally {
            loadingRef.current.contractors = false;
        }
    };

    const handleStatusChange = (vendorId, vendorType) => {
        dispatch(changeVendorStatus(vendorId)).then(() => {
            if (vendorType === 'Supplier') {
                // allow refresh after status change
                fetchedRef.current.suppliers = false;
                getSupplierList();
            } else {
                fetchedRef.current.contractors = false;
                getSubContractorList();
            }
        }).catch(e => console.error(e));
    };

    // Update URL with tab state (no fetch here to avoid redundant calls on sub-tab changes)
    const updateURL = (tab, subTab) => {
        searchParams.set('tab', tab);
        searchParams.set('subTab', subTab);
        navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
    };

    // Handle tab change
    const handleTabChange = (key) => {
        setState(prev => ({ ...prev, activeTab: key }));
        updateURL(key, state.activeSubTab);
        // Fetch only when switching main tab and data for that tab hasn't been loaded yet
        if (key === SUPPLIERS && rSupplierPermission) {
            if (!fetchedRef.current.suppliers && state.activeSuppliers.length === 0 && state.inactiveSuppliers.length === 0) getSupplierList();
        } else if (key === CONTRACTORS && rContractorPermission) {
            if (!fetchedRef.current.contractors && state.activeContractors.length === 0 && state.inactiveContractors.length === 0) getSubContractorList();
        }
    };

    // Handle sub-tab change (active/inactive)
    const handleSubTabChange = (key) => {
        setState(prev => ({ ...prev, activeSubTab: key }));
        // Only update URL; data already present from initial payload
        updateURL(state.activeTab, key);
    };

    const columns = [
        {
            field: "vendorNumber",
            title: "Vendor Number",
            width: "15%",
            dataCy: "vendor-number",
        },
        {
            field: "businessName",
            title: "Company Name",
            width: "20%",
            render: (rowData) => (
                <Link
                    to={`/${VENDOR}/${rowData.type === 'Supplier' ? 'add-supplier' : 'add-contractor'}/edit/${rowData.id}?returnTab=${state.activeTab}&returnSubTab=${state.activeSubTab}`}
                >
                    {rowData.businessName}
                </Link>
            ),
            dataCy: "company-name",
        },
        {
            field: "address",
            title: "Address",
            width: "25%",
            dataCy: "address",
        },
        {
            field: "phoneNumber",
            title: "Phone Number",
            width: "15%",
            dataCy: "phone-number",
        },
        {
            field: "contacts",
            title: "Contact",
            width: "15%",
            dataCy: "contact",
        },
        {
            field: "email",
            title: "Email",
            width: "15%",
            dataCy: "email",
        },
        {
            field: "isActive",
            title: "Active",
            width: "10%",
            lookup: { true: "ACTIVE", false: "INACTIVE" },
            render: (rowData) => (
                <div className="form-check form-switch">
                    <input
                        className={`form-check-input switch-checkbox ${rowData.isActive ? '' : 'inactive'}`}
                        type="checkbox"
                        role="switch"
                        id="flexSwitchCheckDefault"
                        disabled={!writePermission}
                        checked={rowData.isActive}
                        onChange={(e) => handleStatusChange(rowData.id, rowData.type)}
                    />
                </div>
            ),
            dataCy: "active",
        },
        // {
        //     field: "actions",
        //     title: "Actions",
        //     width: "5%",
        //     render: (rowData) => (
        //         <div className="action-edit">
        //             <Link
        //                 to={`/${VENDOR}/${rowData.type === 'Supplier' 
        //                     ? 'add-supplier' : 'add-contractor'}/edit/${rowData.id}?returnTab=${state.activeTab}&returnSubTab=${state.activeSubTab}`}
        //             >
        //                 <Tooltip title="Edit Vendor" placement="left" arrow followCursor={true}>
        //                     <EditIcon />
        //                 </Tooltip>
        //             </Link>
        //         </div>
        //     ),
        //     dataCy: "actions",
        // },
    ];

    const editVendor = (id, type) => {
        navigate(`/${VENDOR}/${type === 'Supplier' ? 'add-supplier' : 'add-contractor'}/edit/${id}?returnTab=${state.activeTab}&returnSubTab=${state.activeSubTab}`);
    };

    const renderVendorTable = (activeData, inactiveData, vendorType) => (
        <Tabs
            activeKey={state.activeSubTab}
            onSelect={handleSubTabChange}
            id="sub-tabs"
            className="mb-3 sub-tabs"
        >
            <Tab eventKey="active" title={`ACTIVE (${activeData.length})`} tabClassName="active-tab">
                <CustomTable
                    columns={columns}
                    data={activeData}
                    // isAction={false}
                    fileName={`${vendorType} List`}
                    dataCy="active-list"
                    isEdit={writePermission}
                    editHandler={(item) => editVendor(item.id, item.type)}
                />
            </Tab>
            <Tab eventKey="inactive" title={`INACTIVE (${inactiveData.length})`} tabClassName="inactive-tab">
                <CustomTable
                    columns={columns}
                    data={inactiveData}
                    // isAction={false}
                    fileName={`${vendorType} List`}
                    dataCy="inactive-list"
                    isEdit={writePermission}
                    editHandler={(item) => editVendor(item.id, item.type)}
                />
            </Tab>
        </Tabs>
    );

    const renderVendorLinks = (vendorType, activeTab, activeSubTab, tabWritePermission) => {
        if (!tabWritePermission) return null;

        const isSupplier = vendorType === SUPPLIERS;
        const addPath = `/${VENDOR}/add-${isSupplier ? VENDOR_TYPE.SUPPLIER : VENDOR_TYPE.CONTRACTOR}`;
        const importPath = `/${VENDOR}/import-${isSupplier ? VENDOR_TAB.SUPPLIERS : VENDOR_TAB.CONTRACTORS}`;
        const addText = `add ${isSupplier ? VENDOR_TYPE.SUPPLIER : VENDOR_TYPE.CONTRACTOR}`;
        const importText = `import ${isSupplier ? VENDOR_TAB.SUPPLIERS : VENDOR_TAB.CONTRACTORS}`;

        return (
            <>
                {isAccountingEnabled && connections.length > 0 && (
                    <button
                        onClick={handleSyncContacts}
                        className="secondarybtn secondarybtn-outline btn-ch btn btn-info text-uppercase"
                        disabled={!tabWritePermission}
                    >
                        {isSupplier ? 'Sync Supplier Contacts' : 'Sync Contractor Contacts'}
                    </button>
                )}
                <Link
                    to={`${addPath}?${VENDOR_URL_PARAM.TAB}=${activeTab}&${VENDOR_URL_PARAM.SUB_TAB}=${activeSubTab}`}
                    className="secondarybtn secondarybtn-outline btn-ch ms-auto btn btn-primary text-uppercase"
                    data-cy={addText}
                >
                    {addText}
                </Link>
                <Link
                    to={`${importPath}?${VENDOR_URL_PARAM.TAB}=${activeTab}&${VENDOR_URL_PARAM.SUB_TAB}=${activeSubTab}`}
                    className="secondarybtn secondarybtn-outline btn-ch ms-auto btn btn-primary text-uppercase"
                    data-cy={importText}
                >
                    {importText}
                </Link>
            </>
        );
    };

    const getActiveVendorsForTab = () => {
        return state.activeTab === SUPPLIERS ? state.activeSuppliers : state.activeContractors;
    };

    return (
        <div className="purchase-order-block vendor-management" data-cy="vendor-management">
            <div className="row">
                <div className="col-12">
                    <h2 className="content-heading text-uppercase" data-cy="heading">VENDORS</h2>
                </div>
            </div>

            <div className="row">
                <div className="col-12">
                    <div className="vendor-header" data-cy="vendor-header">
                        <Tabs
                            activeKey={state.activeTab}
                            id="vendor-tabs"
                            className="vendor-tabs mb-0"
                            onSelect={handleTabChange}
                        >
                            {rSupplierPermission && (
                                <Tab eventKey={SUPPLIERS} title="SUPPLIERS" tabClassName="suppliers" />
                            )}
                            {rContractorPermission && (
                                <Tab eventKey={CONTRACTORS} title="CONTRACTORS" tabClassName="contractors" />
                            )}
                        </Tabs>

                        <div className="d-flex gap-2">
                            {state.activeTab === SUPPLIERS && rSupplierPermission && renderVendorLinks(SUPPLIERS, state.activeTab, state.activeSubTab, wSupplierPermission)}
                            {state.activeTab === CONTRACTORS && rContractorPermission && renderVendorLinks(CONTRACTORS, state.activeTab, state.activeSubTab, wContractorPermission)}
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-12" data-cy="vendor-table">
                    {state.activeTab === SUPPLIERS && rSupplierPermission && (
                        renderVendorTable(state.activeSuppliers, state.inactiveSuppliers, VENDOR_TYPE_LABEL.SUPPLIERS)
                    )}
                    {state.activeTab === CONTRACTORS && rContractorPermission && (
                        renderVendorTable(state.activeContractors, state.inactiveContractors, VENDOR_TYPE_LABEL.CONTRACTORS)
                    )}
                </div>
            </div>
            <SyncContactsModal
                show={showSyncModal}
                onHide={() => setShowSyncModal(false)}
                entityType={state.activeTab === SUPPLIERS ? VENDOR_TYPE.SUPPLIER : VENDOR_TYPE.CONTRACTOR}
                localContacts={getActiveVendorsForTab()}
                onSyncSuccess={handleSyncSuccess}
            />
        </div>
    );
}

export default VendorList;
